"use client"

import React, { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { AccessTime, Person, Build, CheckCircle } from "@mui/icons-material"
import { format, addDays } from "date-fns"
import { fr, enUS } from "date-fns/locale"
import {
  useAppointmentsConsecutiveSlotsRetrieveQuery,
  useTeamsTeamMembersSalonMembersRetrieveQuery,
  useServicesSalonListQuery,
  useAppointmentsCreateMutation,
  useAuthProfileRetrieveQuery,
} from "../../../services/GLOWClient"
import { useAuth } from "../../../context/AuthContext"
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext'
import Button from '../../common/salonPublicAppearance/SalonPublicButton'

const ConsecutiveServiceBooking = ({
  salonId,
  onClose,
  onSuccess,
  salonDetails = null,
}) => {
  const { t, i18n } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const { isAuthenticated } = useAuth()
  const { data: userProfile } = useAuthProfileRetrieveQuery(undefined, { skip: !isAuthenticated() })
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'))

  // State for service and team member selection
  const [service1Id, setService1Id] = useState("")
  const [teamMember1Id, setTeamMember1Id] = useState("")
  const [service2Id, setService2Id] = useState("")
  const [teamMember2Id, setTeamMember2Id] = useState("")
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"))
  const [bufferMinutes, setBufferMinutes] = useState(0)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [isCreating, setIsCreating] = useState(false)

  // Fetch salon services
  const {
    data: servicesData,
    isLoading: servicesLoading,
    error: servicesError,
  } = useServicesSalonListQuery({ salonId }, { skip: !salonId })

  // Fetch team members
  const {
    data: teamMembers,
    isLoading: teamMembersLoading,
    error: teamMembersError,
  } = useTeamsTeamMembersSalonMembersRetrieveQuery({ salon: salonId }, { skip: !salonId })

  // Fetch consecutive slots when all parameters are selected
  const {
    data: consecutiveSlotsData,
    isLoading: slotsLoading,
    error: slotsError,
    refetch: refetchSlots,
  } = useAppointmentsConsecutiveSlotsRetrieveQuery(
    {
      service1Id: parseInt(service1Id),
      teamMember1Id: parseInt(teamMember1Id),
      service2Id: parseInt(service2Id),
      teamMember2Id: parseInt(teamMember2Id),
      date: selectedDate,
      bufferMinutes: bufferMinutes,
    },
    {
      skip: !service1Id || !teamMember1Id || !service2Id || !teamMember2Id || !selectedDate,
    }
  )

  // Create appointment mutation
  const [createAppointment] = useAppointmentsCreateMutation()

  // Filter team members who can provide each service
  const getTeamMembersForService = (serviceId) => {
    if (!teamMembers || !serviceId) return []
    return teamMembers.filter((member) => 
      member.service_ids && member.service_ids.includes(parseInt(serviceId))
    )
  }

  const getApiErrorMessage = (error, defaultMessage) => {
    if (!error) return defaultMessage
    const data = error.data || error.error || error.message
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      if (Array.isArray(data.details) && data.details.length > 0) {
        return data.details.join("\n")
      }
      if (typeof data.details === "string") return data.details
      if (typeof data.error === "string") return data.error
    }
    return defaultMessage
  }

  const filterConsecutiveSlotsForDate = (slots, dateStr) => {
    if (!Array.isArray(slots)) return []
    if (!dateStr) return slots

    const todayStr = format(new Date(), "yyyy-MM-dd")
    if (dateStr !== todayStr) {
      return slots
    }

    const now = new Date()
    return slots.filter((slot) => {
      if (!slot.start_time) return true
      const [hours, minutes] = slot.start_time.split(":").map(Number)
      const slotDateTime = new Date()
      slotDateTime.setHours(hours || 0, minutes || 0, 0, 0)
      return slotDateTime >= now
    })
  }

  // Generate date options for the next 30 days
  const getDateOptions = () => {
    const dateFnsLocale = i18n.language === 'en' ? enUS : fr
    const dates = []
    for (let i = 0; i < 30; i++) {
      const date = addDays(new Date(), i)
      dates.push({
        value: format(date, "yyyy-MM-dd"),
        label: format(date, "EEEE d MMMM", { locale: dateFnsLocale }),
      })
    }
    return dates
  }

  // Handle service selection changes
  const handleService1Change = (event) => {
    setService1Id(event.target.value)
    setTeamMember1Id("") // Reset team member selection
    setSelectedSlot(null) // Reset slot selection
  }

  const handleService2Change = (event) => {
    setService2Id(event.target.value)
    setTeamMember2Id("") // Reset team member selection
    setSelectedSlot(null) // Reset slot selection
  }

  const handleTeamMember1Change = (event) => {
    setTeamMember1Id(event.target.value)
    setSelectedSlot(null) // Reset slot selection
  }

  const handleTeamMember2Change = (event) => {
    setTeamMember2Id(event.target.value)
    setSelectedSlot(null) // Reset slot selection
  }

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value)
    setSelectedSlot(null) // Reset slot selection
  }

  const handleBufferChange = (event) => {
    setBufferMinutes(event.target.value)
    setSelectedSlot(null) // Reset slot selection
  }

  // Handle slot selection
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
  }

  // Handle booking confirmation
  const handleConfirmBooking = async () => {
    if (!selectedSlot || !userProfile) return

    setIsCreating(true)

    try {
      // Create first appointment
      const service1Duration = consecutiveSlotsData?.debug_info?.service1_info?.duration || 60
      const service2Duration = consecutiveSlotsData?.debug_info?.service2_info?.duration || 60

      const service1StartDateTime = new Date(`${selectedDate}T${selectedSlot.service1_start}:00`)
      const service1EndDateTime = new Date(`${selectedDate}T${selectedSlot.service1_end}:00`)
      const service2StartDateTime = new Date(`${selectedDate}T${selectedSlot.service2_start}:00`)
      const service2EndDateTime = new Date(`${selectedDate}T${selectedSlot.service2_end}:00`)

      // Format dates for API
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        const hours = String(date.getHours()).padStart(2, "0")
        const minutes = String(date.getMinutes()).padStart(2, "0")
        const seconds = String(date.getSeconds()).padStart(2, "0")
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
      }

      // Create both appointments
      const appointment1Data = {
        salon: salonId,
        service: parseInt(service1Id),
        team_member: parseInt(teamMember1Id),
        client: userProfile.id,
        start_time: formatLocalDateTime(service1StartDateTime),
        end_time: formatLocalDateTime(service1EndDateTime),
      }

      const appointment2Data = {
        salon: salonId,
        service: parseInt(service2Id),
        team_member: parseInt(teamMember2Id),
        client: userProfile.id,
        start_time: formatLocalDateTime(service2StartDateTime),
        end_time: formatLocalDateTime(service2EndDateTime),
      }

      // Create both appointments
      const [result1, result2] = await Promise.all([
        createAppointment({ appointmentCreateRequest: appointment1Data }).unwrap(),
        createAppointment({ appointmentCreateRequest: appointment2Data }).unwrap(),
      ])

      // Call success callback
      if (onSuccess) {
        onSuccess([result1, result2])
      }

    } catch (error) {
      console.error("Failed to create consecutive appointments:", error)
    } finally {
      setIsCreating(false)
    }
  }

  // Get service name by ID
  const getServiceName = (serviceId) => {
    if (!servicesData?.results || !serviceId) return ""
    const service = servicesData.results.find(s => s.service_id === parseInt(serviceId))
    return service?.name || ""
  }

  // Get team member name by ID
  const getTeamMemberName = (teamMemberId) => {
    if (!teamMembers || !teamMemberId) return ""
    const member = teamMembers.find(m => m.team_member_id === parseInt(teamMemberId))
    return member ? `${member.user_details?.first_name} ${member.user_details?.last_name}` : ""
  }

  const visibleConsecutiveSlots = consecutiveSlotsData
    ? filterConsecutiveSlotsForDate(consecutiveSlotsData.consecutive_slots, selectedDate)
    : []

  return (
    <Box
      style={{
        backgroundColor: theme.salon.background,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
      }}
      sx={{ p: { xs: 2, md: 3 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.colors.text.primary,
            mb: 1,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Réservation de services consécutifs
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.colors.text.secondary,
            mb: 2,
          }}
        >
          Sélectionnez deux services avec deux employés différents pour une réservation en continu
        </Typography>
        
        {salonDetails && (
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
              color: "white",
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {salonDetails.name}
              </Typography>
              {salonDetails.address && (
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  📍 {salonDetails.address}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Service Selection */}
      <Grid container spacing={3}>
        {/* First Service */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, border: `2px solid ${theme.colors.primary[200]}` }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: theme.colors.primary[700] }}
            >
              Premier service
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Service</InputLabel>
              <Select
                value={service1Id}
                onChange={handleService1Change}
                label="Service"
                disabled={servicesLoading}
              >
                {servicesData?.results?.map((service) => (
                  <MenuItem key={service.service_id} value={service.service_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Build fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {service.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.duration} min - {service.price} DA
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Employé</InputLabel>
              <Select
                value={teamMember1Id}
                onChange={handleTeamMember1Change}
                label="Employé"
                disabled={!service1Id || teamMembersLoading}
              >
                {getTeamMembersForService(service1Id).map((member) => (
                  <MenuItem key={member.team_member_id} value={member.team_member_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      <Typography>
                        {member.user_details?.first_name} {member.user_details?.last_name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        </Grid>

        {/* Second Service */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, border: `2px solid ${theme.colors.secondary[200]}` }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: theme.colors.secondary[700] }}
            >
              Deuxième service
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Service</InputLabel>
              <Select
                value={service2Id}
                onChange={handleService2Change}
                label="Service"
                disabled={servicesLoading}
              >
                {servicesData?.results?.map((service) => (
                  <MenuItem key={service.service_id} value={service.service_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Build fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {service.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.duration} min - {service.price}DA
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Employé</InputLabel>
              <Select
                value={teamMember2Id}
                onChange={handleTeamMember2Change}
                label="Employé"
                disabled={!service2Id || teamMembersLoading}
              >
                {getTeamMembersForService(service2Id).map((member) => (
                  <MenuItem key={member.team_member_id} value={member.team_member_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      <Typography>
                        {member.user_details?.first_name} {member.user_details?.last_name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        </Grid>
      </Grid>

      {/* Date and Buffer Selection */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Date</InputLabel>
            <Select
              value={selectedDate}
              onChange={handleDateChange}
              label="Date"
            >
              {getDateOptions().map((date) => (
                <MenuItem key={date.value} value={date.value}>
                  {date.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Temps de pause entre services</InputLabel>
            <Select
              value={bufferMinutes}
              onChange={handleBufferChange}
              label="Temps de pause entre services"
            >
              <MenuItem value={0}>Aucune pause (consécutif)</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>1 heure</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Available Consecutive Slots */}
      {service1Id && teamMember1Id && service2Id && teamMember2Id && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, color: theme.colors.text.primary }}
          >
            {t('client_services.available_time_slots')}
          </Typography>

          {slotsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {slotsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getApiErrorMessage(slotsError, "Erreur lors du chargement des créneaux disponibles")}
            </Alert>
          )}

          {consecutiveSlotsData && (
            <>
              {visibleConsecutiveSlots.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Aucun créneau consécutif   pour cette combinaison de services et employés.
                  Essayez une autre date ou modifiez le temps de pause.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {visibleConsecutiveSlots.map((slot, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: selectedSlot === slot ? `2px solid ${theme.salon.primary}` : `1px solid ${theme.colors.border.light}`,
                          backgroundColor: selectedSlot === slot ? theme.colors.primary[50] : 'white',
                          '&:hover': {
                            borderColor: theme.colors.primary[300],
                            backgroundColor: theme.colors.primary[50],
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => handleSlotSelect(slot)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AccessTime fontSize="small" color="primary" />
                            <Typography variant="h6" fontWeight={600}>
                              {slot.start_time} - {slot.end_time}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              label={`${getServiceName(service1Id)} (${slot.service1_start} - ${slot.service1_end})`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 1, mr: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {getTeamMemberName(teamMember1Id)}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Chip
                              label={`${getServiceName(service2Id)} (${slot.service2_start} - ${slot.service2_end})`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ mb: 1, mr: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {getTeamMemberName(teamMember2Id)}
                            </Typography>
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('client_services.total_duration')}: {slot.total_duration} {t('client_services.min')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isCreating}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmBooking}
          disabled={!selectedSlot || isCreating || !isAuthenticated()}
          startIcon={isCreating ? <CircularProgress size={20} /> : <CheckCircle />}
          sx={{
            background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[800]} 100%)`,
            },
          }}
        >
          {isCreating ? 'Création...' : 'Confirmer la réservation'}
        </Button>
      </Box>
    </Box>
  )
}

export default ConsecutiveServiceBooking
